import * as THREE from 'three';

/** Hemisphere search radius as a physical length in feet (3 in = 0.25 ft). */
export const HEMISPHERE_RADIUS_FT = 3 / 12;

/** Potree LengthUnits.FEET.unitspermeter — feet per metre. */
const FEET_PER_METER = 3.28084;

/**
 * Convert the physical hemisphere radius (feet) into the point cloud's native
 * scene units. On a metric cloud one scene unit is a metre, so 0.2 ft must
 * become ~0.061 scene units instead of 0.2. Reads viewer.lengthUnit (set from
 * the cloud's projection); falls back to the raw feet value when the unit is
 * unknown, preserving the previous behaviour.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function hemisphereRadiusScene(viewer: any, radiusFt = HEMISPHERE_RADIUS_FT): number {
    const unitsPerMeter = viewer?.lengthUnit?.unitspermeter;
    if (typeof unitsPerMeter === 'number' && unitsPerMeter > 0) {
        return radiusFt * (unitsPerMeter / FEET_PER_METER);
    }
    return radiusFt;
}

export interface PointData {
    position: { x: number; y: number; z: number };
    distanceToMarker: number;
    attributes: Record<string, number | number[]>;
}

export interface NeighborSummary {
    /** Points used for z-spread / dist (all unique in hemisphere) */
    n: number;
    /** Unique points inside the hemisphere (after dedupe) */
    inWindow: number;
    zSpread: number | null;
    /** Distance from original marker to representative point P′ */
    distance: number | null;
    /** Representative point P′ (component-wise median of neighbors) */
    centroid: { x: number; y: number; z: number } | null;
    /** RMS of neighbor distances to the local best-fit plane (slope-independent roughness). */
    residualSpread: number | null;
    /** Clicked point's vertical distance to that plane (flying-point indicator). */
    verticalOffset: number | null;
    /**
     * Tilt of the local best-fit plane, tan(angle from horizontal) = |gradient|.
     * A ramp is ~0.05–0.15; a near-vertical curb face / wall is >> 1. Lets us tell
     * "measured a vertical face" apart from a merely rough surface. Null if the fit
     * was degenerate or had <3 points.
     */
    planeTilt: number | null;
    /** Main drop / marker point (before snap to P′) */
    marker: PointData;
    /** Neighbor points used for analysis (nearest first) */
    neighbors: PointData[];
}

export interface MarkerNeighborData {
    start: NeighborSummary | null;
    end: NeighborSummary | null;
}

export interface NearbyPointsResult {
    neighbors: THREE.Vector3[];
    neighborData: PointData[];
    inWindow: number;
}

function positionKey(position: THREE.Vector3, decimals = 4): string {
    return `${position.x.toFixed(decimals)},${position.y.toFixed(decimals)},${position.z.toFixed(decimals)}`;
}

function stddev(values: number[]): number {
    if (values.length === 0) return 0;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
    return Math.sqrt(variance);
}

/** Component-wise median (sort copy; average the two middle values when n is even). */
function median(values: number[]): number {
    if (values.length === 0) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    if (sorted.length % 2 === 1) {
        return sorted[mid];
    }
    return (sorted[mid - 1] + sorted[mid]) / 2;
}

function readAttributesAtIndex(
    geometry: THREE.BufferGeometry,
    index: number
): Record<string, number | number[]> {
    const attributes: Record<string, number | number[]> = {};

    for (const name of Object.keys(geometry.attributes)) {
        if (name === 'position' || name === 'indices') continue;
        const attribute = geometry.attributes[name] as any;
        if (!attribute?.array || !attribute.itemSize) continue;

        let values = Array.from(
            attribute.array.slice(
                attribute.itemSize * index,
                attribute.itemSize * (index + 1)
            )
        ) as number[];

        if (attribute.potree) {
            const { scale, offset } = attribute.potree;
            values = values.map((v) => v / scale + offset);
        }

        attributes[name] = values.length === 1 ? values[0] : values;
    }

    return attributes;
}

export function markerToPointData(markerPoint: any): PointData {
    const positionVec = markerPoint.position as THREE.Vector3;
    const attributes: Record<string, number | number[]> = {};

    for (const key of Object.keys(markerPoint)) {
        if (key === 'position') continue;
        const value = markerPoint[key];
        if (Array.isArray(value)) {
            attributes[key] = Array.from(value as ArrayLike<number>);
        } else if (typeof value === 'number') {
            attributes[key] = value;
        }
    }

    return {
        position: {
            x: positionVec.x,
            y: positionVec.y,
            z: positionVec.z,
        },
        distanceToMarker: 0,
        attributes,
    };
}

/**
 * Prefer Potree's binary buffer (same path as getPointsInBox).
 * geometry.attributes can be missing/stale right after a marker drop.
 */
function forEachNodePoint(
    node: any,
    callback: (worldPos: THREE.Vector3, localIndex: number) => void
) {
    const sceneNode = node.sceneNode;
    const geometryNode = node.geometryNode;
    if (!sceneNode || !geometryNode) return;

    // Do NOT force updateMatrixWorld here — calling it during mouseup
    // can disturb EarthControls and shift the camera.
    const matrixWorld: THREE.Matrix4 = sceneNode.matrixWorld;
    const worldPos = new THREE.Vector3();

    const buffer = geometryNode.buffer;
    if (buffer?.data && typeof buffer.offset === 'function' && buffer.numElements > 0) {
        const posOffset = buffer.offset('position');
        const stride = buffer.stride;
        const view = new DataView(buffer.data);
        for (let i = 0; i < buffer.numElements; i++) {
            const x = view.getFloat32(i * stride + posOffset + 0, true);
            const y = view.getFloat32(i * stride + posOffset + 4, true);
            const z = view.getFloat32(i * stride + posOffset + 8, true);
            worldPos.set(x, y, z).applyMatrix4(matrixWorld);
            callback(worldPos, i);
        }
        return;
    }

    const geometry = geometryNode.geometry as THREE.BufferGeometry | undefined;
    const positionAttr = geometry?.attributes?.position;
    if (!positionAttr?.array) return;

    const positions = positionAttr.array as ArrayLike<number>;
    const numPoints = positionAttr.count ?? Math.floor(positions.length / 3);
    for (let i = 0; i < numPoints; i++) {
        worldPos
            .set(positions[3 * i], positions[3 * i + 1], positions[3 * i + 2])
            .applyMatrix4(matrixWorld);
        callback(worldPos, i);
    }
}

/** Read points from a geometry node using pointcloud.matrixWorld * T(bbox.min). */
function forEachGeometryNodePoint(
    pointcloud: any,
    geometryNode: any,
    callback: (worldPos: THREE.Vector3, localIndex: number) => void
) {
    const box: THREE.Box3 | undefined =
        geometryNode.boundingBox || geometryNode.getBoundingBox?.();
    if (!box || !pointcloud?.matrixWorld) return;

    const matrix = new THREE.Matrix4()
        .copy(pointcloud.matrixWorld)
        .multiply(
            new THREE.Matrix4().makeTranslation(box.min.x, box.min.y, box.min.z)
        );
    const worldPos = new THREE.Vector3();

    const buffer = geometryNode.buffer;
    if (buffer?.data && typeof buffer.offset === 'function' && buffer.numElements > 0) {
        const posOffset = buffer.offset('position');
        const stride = buffer.stride;
        const view = new DataView(buffer.data);
        for (let i = 0; i < buffer.numElements; i++) {
            const x = view.getFloat32(i * stride + posOffset + 0, true);
            const y = view.getFloat32(i * stride + posOffset + 4, true);
            const z = view.getFloat32(i * stride + posOffset + 8, true);
            worldPos.set(x, y, z).applyMatrix4(matrix);
            callback(worldPos, i);
        }
        return;
    }

    const geometry = geometryNode.geometry as THREE.BufferGeometry | undefined;
    const positionAttr = geometry?.attributes?.position;
    if (!positionAttr?.array) return;

    const positions = positionAttr.array as ArrayLike<number>;
    const numPoints = positionAttr.count ?? Math.floor(positions.length / 3);
    for (let i = 0; i < numPoints; i++) {
        worldPos
            .set(positions[3 * i], positions[3 * i + 1], positions[3 * i + 2])
            .applyMatrix4(matrix);
        callback(worldPos, i);
    }
}

function geometryNodeChildren(geometryNode: any): any[] {
    if (typeof geometryNode.getChildren === 'function') {
        return geometryNode.getChildren();
    }
    const children = geometryNode.children;
    if (!children) return [];
    if (Array.isArray(children)) return children.filter(Boolean);
    return Object.values(children).filter(Boolean);
}

function geometryNodeIntersectsSphere(
    pointcloud: any,
    geometryNode: any,
    searchSphere: THREE.Sphere
): boolean {
    const localBox: THREE.Box3 | undefined =
        geometryNode.boundingBox || geometryNode.getBoundingBox?.();
    if (!localBox || !pointcloud.matrixWorld) return true;
    const worldBox = localBox.clone().applyMatrix4(pointcloud.matrixWorld);
    return worldBox.intersectsSphere(searchSphere);
}

function geometryNodeSpacing(pointcloud: any, geometryNode: any): number {
    if (typeof geometryNode.spacing === 'number' && geometryNode.spacing > 0) {
        return geometryNode.spacing;
    }
    const base = pointcloud.pcoGeometry?.spacing;
    const level =
        typeof geometryNode.level === 'number'
            ? geometryNode.level
            : Math.max(0, (geometryNode.name?.length ?? 1) - 1);
    if (typeof base === 'number' && base > 0) {
        return base / Math.pow(2, level);
    }
    return Number.POSITIVE_INFINITY;
}

/**
 * Request denser octree nodes around a world-space sample so neighbor counts
 * are not limited to whatever coarse LOD is currently on screen.
 */
export async function ensureNeighborhoodLoaded(
    viewer: any,
    centerInput: THREE.Vector3,
    radius = HEMISPHERE_RADIUS_FT,
    options?: { timeoutMs?: number; targetSpacingFactor?: number }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any[]> {
    const center = centerInput.clone();
    // Geometry nodes this call forced to load. Returned so the caller can release
    // them after the neighbor read: Potree only LRU-tracks nodes it actually
    // renders/queries, so a force-loaded-but-invisible node is never evicted and
    // leaks GPU memory until the WebGL context is lost (the "white screen").
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const forceLoaded = new Set<any>();
    const searchSphere = new THREE.Sphere(center, radius);
    const timeoutMs = options?.timeoutMs ?? 2500;
    const targetSpacing = radius * (options?.targetSpacingFactor ?? 0.25);
    const deadline = performance.now() + timeoutMs;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Potree = (window as any).Potree;
    const prevMaxLoading =
        typeof Potree?.maxNodesLoading === 'number' ? Potree.maxNodesLoading : null;
    if (Potree && typeof Potree.maxNodesLoading === 'number') {
        Potree.maxNodesLoading = Math.max(Potree.maxNodesLoading, 12);
    }

    const sleep = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));

    try {
        while (performance.now() < deadline) {
            let pendingLoads = 0;

            for (const pointcloud of viewer.scene.pointclouds || []) {
                const root = pointcloud.pcoGeometry?.root;
                if (!root) continue;

                const visit = (geometryNode: any) => {
                    if (!geometryNodeIntersectsSphere(pointcloud, geometryNode, searchSphere)) {
                        return;
                    }

                    if (!geometryNode.loaded) {
                        if (!geometryNode.loading && typeof geometryNode.load === 'function') {
                            geometryNode.load();
                            forceLoaded.add(geometryNode);
                        }
                        pendingLoads++;
                        return;
                    }

                    const spacing = geometryNodeSpacing(pointcloud, geometryNode);
                    const children = geometryNodeChildren(geometryNode);
                    const mayHaveChildren =
                        children.length > 0 || geometryNode.hasChildren === true;

                    if (spacing > targetSpacing && mayHaveChildren) {
                        if (children.length === 0 && typeof geometryNode.load === 'function') {
                            // Hierarchy may still be expanding.
                            if (!geometryNode.loading) {
                                geometryNode.load();
                                forceLoaded.add(geometryNode);
                            }
                            pendingLoads++;
                            return;
                        }
                        for (const child of children) {
                            visit(child);
                        }
                    }
                };

                visit(root);
            }

            if (pendingLoads === 0) return Array.from(forceLoaded);
            await sleep(50);
        }
    } finally {
        if (Potree && prevMaxLoading !== null) {
            Potree.maxNodesLoading = prevMaxLoading;
        }
    }

    return Array.from(forceLoaded);
}

/**
 * Release geometry nodes that `ensureNeighborhoodLoaded` force-loaded, once the
 * neighbor read is done. Only nodes NOT in Potree's LRU are disposed — anything
 * the renderer or a query is actually using has been LRU-touched and is left to
 * Potree's own eviction. This is the missing counterpart to the force-load:
 * without it, invisible force-loaded nodes are never evicted and pile up until
 * the WebGL context is lost.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function releaseForceLoadedNodes(nodes: any[]): void {
    if (!nodes || nodes.length === 0) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const Potree = (window as any).Potree;
    const lruItems = Potree?.lru?.items;
    let released = 0;
    for (const node of nodes) {
        // Only free what finished loading and is not the (undisposable) root.
        if (!node || node.loaded !== true || node.parent == null) continue;
        // Skip nodes Potree is using — present in the LRU means rendered/queried.
        if (lruItems && lruItems[node.id]) continue;
        if (typeof node.dispose === 'function') {
            node.dispose();
            released++;
        }
    }
    // eslint-disable-next-line no-console
    console.debug(
        `[NeighborValidation] released ${released}/${nodes.length} force-loaded octree node(s)`
    );
}

/**
 * Walk loaded geometry nodes that intersect the search sphere.
 * Prefer deepest loaded nodes (skip a node when a loaded child also intersects).
 */
function forEachLoadedNeighborhoodPoint(
    pointcloud: any,
    searchSphere: THREE.Sphere,
    callback: (worldPos: THREE.Vector3, localIndex: number) => void
) {
    const root = pointcloud.pcoGeometry?.root;
    if (!root) {
        // Fallback: currently rendered tree nodes only.
        for (const node of pointcloud.visibleNodes || []) {
            if (!node.sceneNode || !node.geometryNode) continue;
            const localBox =
                (typeof node.getBoundingBox === 'function' ? node.getBoundingBox() : null) ||
                node.geometryNode.boundingBox;
            if (localBox && pointcloud.matrixWorld) {
                const worldBox = localBox.clone().applyMatrix4(pointcloud.matrixWorld);
                if (!worldBox.intersectsSphere(searchSphere)) continue;
            }
            forEachNodePoint(node, callback);
        }
        return;
    }

    const visit = (geometryNode: any) => {
        if (!geometryNodeIntersectsSphere(pointcloud, geometryNode, searchSphere)) {
            return;
        }
        if (!geometryNode.loaded) return;

        const children = geometryNodeChildren(geometryNode).filter(
            (child) =>
                child.loaded &&
                geometryNodeIntersectsSphere(pointcloud, child, searchSphere)
        );

        if (children.length > 0) {
            for (const child of children) {
                visit(child);
            }
            return;
        }

        // Use geometry-space transform (bbox.min + local) so we do not inject
        // extra tree/scene nodes into Potree's render graph.
        forEachGeometryNodePoint(pointcloud, geometryNode, callback);
    };

    visit(root);
}

/**
 * Collect unique points inside a hemisphere centered at `center`,
 * facing toward `toward`, with radius HEMISPHERE_RADIUS_FT.
 * Keep: distance <= radius AND (P - center) · (toward - center) >= 0
 *
 * Uses loaded octree geometry near the sample (not only on-screen visibleNodes),
 * so far-away drops can still see dense neighbors after ensureNeighborhoodLoaded.
 */
export function findHemisphereNeighbors(
    viewer: any,
    centerInput: THREE.Vector3,
    towardInput: THREE.Vector3,
    radius = HEMISPHERE_RADIUS_FT
): NearbyPointsResult {
    // Clone — deferred analyze must not see mutated marker vectors.
    const center = centerInput.clone();
    const toward = towardInput.clone();

    const direction = new THREE.Vector3().subVectors(toward, center);
    if (direction.lengthSq() < 1e-12) {
        return { neighbors: [], neighborData: [], inWindow: 0 };
    }
    direction.normalize();

    const radiusSq = radius * radius;
    const searchSphere = new THREE.Sphere(center, radius);
    const uniqueByPos = new Map<string, PointData>();
    const offset = new THREE.Vector3();

    let pointsInSphere = 0;
    let pointsRejectedHemisphere = 0;

    for (const pointcloud of viewer.scene.pointclouds || []) {
        forEachLoadedNeighborhoodPoint(pointcloud, searchSphere, (worldPos) => {
            offset.subVectors(worldPos, center);
            const distSq = offset.lengthSq();
            if (distSq > radiusSq) return;

            pointsInSphere++;

            // Closed hemisphere: keep the half facing the other endpoint (incl. plane).
            if (offset.dot(direction) < 0) {
                pointsRejectedHemisphere++;
                return;
            }

            const key = positionKey(worldPos);
            if (uniqueByPos.has(key)) return;

            uniqueByPos.set(key, {
                position: { x: worldPos.x, y: worldPos.y, z: worldPos.z },
                distanceToMarker: Math.sqrt(distSq),
                attributes: {},
            });
        });
    }

    const unique = Array.from(uniqueByPos.values());
    unique.sort((a, b) => a.distanceToMarker - b.distanceToMarker);

    if (unique.length === 0) {
        // eslint-disable-next-line no-console
        console.warn('[NeighborValidation] hemisphere found 0 points', {
            center: { x: center.x, y: center.y, z: center.z },
            toward: { x: toward.x, y: toward.y, z: toward.z },
            radius,
            pointsInSphere,
            pointsRejectedHemisphere,
            pointcloudCount: viewer.scene.pointclouds?.length ?? 0,
        });
    }

    const used = unique;
    return {
        inWindow: unique.length,
        neighborData: used,
        neighbors: used.map(
            (p) => new THREE.Vector3(p.position.x, p.position.y, p.position.z)
        ),
    };
}

/**
 * Least-squares plane fit of the neighbors (deterministic — NOT RANSAC — and
 * diagnostic only: it never feeds the measurement value). Coordinates are
 * centered on the marker so the normal equations stay well-conditioned despite
 * large projected coordinates. Returns:
 *   residualSpread   — RMS of neighbor distances to the fitted plane (roughness
 *                      after the local tilt is removed, so it is slope-independent).
 *   verticalOffset — how far the clicked point sits above/below that plane
 *                      (a flyer indicator, immune to the hemisphere's horizontal pull).
 * Both null when there are too few points (<3) or the fit is degenerate.
 */
function fitPlaneResiduals(
    marker: PointData,
    neighbors: PointData[]
): { residualSpread: number | null; verticalOffset: number | null; planeTilt: number | null } {
    const n = neighbors.length;
    if (n < 3) return { residualSpread: null, verticalOffset: null, planeTilt: null };

    const ox = marker.position.x;
    const oy = marker.position.y;
    const oz = marker.position.z;

    let Sxx = 0, Sxy = 0, Syy = 0, Sx = 0, Sy = 0;
    let Sxz = 0, Syz = 0, Sz = 0;
    for (const p of neighbors) {
        const x = p.position.x - ox;
        const y = p.position.y - oy;
        const z = p.position.z - oz;
        Sxx += x * x;
        Sxy += x * y;
        Syy += y * y;
        Sx += x;
        Sy += y;
        Sxz += x * z;
        Syz += y * z;
        Sz += z;
    }

    // Solve M·[a b c]^T = [Sxz Syz Sz]^T for z = a·x + b·y + c (marker-local).
    // M = | Sxx Sxy Sx |
    //     | Sxy Syy Sy |
    //     | Sx  Sy  n  |
    const det =
        Sxx * (Syy * n - Sy * Sy) -
        Sxy * (Sxy * n - Sy * Sx) +
        Sx * (Sxy * Sy - Syy * Sx);
    if (!Number.isFinite(det) || Math.abs(det) < 1e-12) {
        return { residualSpread: null, verticalOffset: null, planeTilt: null };
    }

    const detA =
        Sxz * (Syy * n - Sy * Sy) -
        Sxy * (Syz * n - Sy * Sz) +
        Sx * (Syz * Sy - Syy * Sz);
    const detB =
        Sxx * (Syz * n - Sy * Sz) -
        Sxz * (Sxy * n - Sy * Sx) +
        Sx * (Sxy * Sz - Syz * Sx);
    const detC =
        Sxx * (Syy * Sz - Sy * Syz) -
        Sxy * (Sxy * Sz - Sx * Syz) +
        Sxz * (Sxy * Sy - Syy * Sx);

    const a = detA / det;
    const b = detB / det;
    const c = detC / det;

    // Plane is z = a·x + b·y + c, so (a, b) is the surface gradient and
    // sqrt(a² + b²) = tan(tilt from horizontal) — unitless rise/run. A ramp is
    // single-digit percent (~0.05–0.15); a curb face / wall is near-vertical
    // (>> 1), which cleanly separates "measured a wall" from "measured the ground".
    const planeTilt = Math.sqrt(a * a + b * b);

    let sumSq = 0;
    for (const p of neighbors) {
        const x = p.position.x - ox;
        const y = p.position.y - oy;
        const z = p.position.z - oz;
        const r = z - (a * x + b * y + c);
        sumSq += r * r;
    }
    const residualSpread = Math.sqrt(sumSq / n);

    // Marker is the local origin (0,0,0); the plane predicts z = c there.
    const verticalOffset = Math.abs(c);

    return { residualSpread, verticalOffset, planeTilt };
}

export function computeNeighborSummary(
    marker: PointData,
    neighbors: PointData[],
    inWindow: number
): NeighborSummary {
    const n = neighbors.length;
    if (n === 0) {
        return {
            n: 0,
            inWindow,
            zSpread: null,
            distance: null,
            centroid: null,
            residualSpread: null,
            verticalOffset: null,
            planeTilt: null,
            marker,
            neighbors: [],
        };
    }

    const zValues = neighbors.map((p) => p.position.z);
    const zSpread = stddev(zValues);

    // P′ = component-wise median of neighbor positions (robust to outliers).
    const pPrime = {
        x: median(neighbors.map((p) => p.position.x)),
        y: median(neighbors.map((p) => p.position.y)),
        z: median(zValues),
    };
    const pPrimeVec = new THREE.Vector3(pPrime.x, pPrime.y, pPrime.z);
    const markerVec = new THREE.Vector3(marker.position.x, marker.position.y, marker.position.z);
    const distance = markerVec.distanceTo(pPrimeVec);

    const { residualSpread, verticalOffset, planeTilt } = fitPlaneResiduals(marker, neighbors);

    return {
        n,
        inWindow,
        zSpread,
        distance,
        centroid: pPrime,
        residualSpread,
        verticalOffset,
        planeTilt,
        marker,
        neighbors,
    };
}

export interface NeighborAnalysis {
    summary: NeighborSummary;
    neighbors: THREE.Vector3[];
}

export function analyzeMarkerNeighbors(
    viewer: any,
    markerPoint: any,
    towardPosition: THREE.Vector3
): NeighborAnalysis {
    const markerPosition = markerPoint.position as THREE.Vector3;
    const marker = markerToPointData(markerPoint);
    const { neighbors, neighborData, inWindow } = findHemisphereNeighbors(
        viewer,
        markerPosition,
        towardPosition,
        hemisphereRadiusScene(viewer)
    );

    return {
        summary: computeNeighborSummary(marker, neighborData, inWindow),
        neighbors,
    };
}

export function printNeighborDebug(
    label: 'Start' | 'End',
    summary: NeighborSummary | null
) {
    if (!summary) {
        // eslint-disable-next-line no-console
        console.log(`[NeighborValidation] ${label}: (not set yet)`);
        return;
    }

    // eslint-disable-next-line no-console
    console.log(`[NeighborValidation] ${label}`, {
        hemisphereRadiusFt: HEMISPHERE_RADIUS_FT,
        uniqueInHemisphere: summary.inWindow,
        nUsed: summary.n,
        zSpread: summary.zSpread,
        distToCentroid: summary.distance,
        centroid: summary.centroid,
        marker: summary.marker,
        neighbors: summary.neighbors,
    });
}

/** Distance, height & slope between two representative points (Potree Measure convention). */
export function computeDistanceAndSlope(
    a: { x: number; y: number; z: number },
    b: { x: number; y: number; z: number }
): { distance: number; height: number; slopePercent: number } {
    const A = new THREE.Vector3(a.x, a.y, a.z);
    const B = new THREE.Vector3(b.x, b.y, b.z);
    const distance = A.distanceTo(B);
    const height = Math.abs(B.z - A.z);
    const low = A.z <= B.z ? A : B;
    const high = A.z <= B.z ? B : A;
    const flat = new THREE.Vector3(high.x, high.y, low.z);
    const run = low.distanceTo(flat);
    const slopePercent = run < 1e-12 ? 0 : (height / run) * 100;
    return { distance, height, slopePercent };
}

// A measurement steeper than this (angle from horizontal) is treated as vertical:
// e.g. the height of a sign or pole. There is no local surface to smooth, and the
// slope projection below is ill-conditioned there (the medians' horizontal run → 0,
// so slope = Δz/run explodes and amplifies horizontal median noise into a huge
// vertical error, making Smoothed distance diverge wildly from Standard). Above this
// angle we skip the projection and use the raw clicks, so Smoothed distance ≈ Standard.
const VERTICAL_ANGLE_DEG = 75;
// run/rise threshold = tan(90° − 75°) = tan(15°) ≈ 0.268.
const VERTICAL_RUN_OVER_RISE = Math.tan(((90 - VERTICAL_ANGLE_DEG) * Math.PI) / 180);

/**
 * Is the line between two points near-vertical (≥ VERTICAL_ANGLE_DEG from
 * horizontal)? e.g. a sign / pole height. Used to switch Smoothed into a
 * "vertical measurement" mode where surface smoothing/quality don't apply.
 */
export function isNearVertical(
    a: { x: number; y: number; z: number },
    b: { x: number; y: number; z: number }
): boolean {
    const run = Math.hypot(b.x - a.x, b.y - a.y);
    const rise = Math.abs(b.z - a.z);
    return run <= rise * VERTICAL_RUN_OVER_RISE;
}

/**
 * §11 slope projection. The raw XYZ median (`centroid`) is pulled toward the other
 * endpoint by the one-sided hemisphere, which systematically shortens the reported
 * distance. Keep each endpoint's horizontal position at the click P and only project
 * the median height back along the slope, so distance ≈ P↔P while the de-noised
 * height stays correctly placed (slope also stays unbiased).
 *
 *   P′.x, P′.y = P.x, P.y                     (the original click's horizontal position)
 *   P′.z       = median_z + s · d
 *     s = slope from the two raw medians (Δz / horizontal run)
 *     d = horizontal offset (P − median) projected onto the line direction
 *
 * Near-vertical measurements (≥ VERTICAL_ANGLE_DEG from horizontal, judged from the
 * stable click geometry — e.g. a sign / pole height) have no surface to smooth and
 * make the projection ill-conditioned, so we fall back to the raw clicks (Smoothed ≈
 * Standard). Also falls back to the raw medians if they are horizontally coincident.
 * Returns null when either centroid is missing.
 */
export function correctPPrimeForSlope(
    start: NeighborSummary,
    end: NeighborSummary
): {
    start: { x: number; y: number; z: number };
    end: { x: number; y: number; z: number };
} | null {
    const rawStart = start.centroid;
    const rawEnd = end.centroid;
    if (!rawStart || !rawEnd) return null;

    const clickStart = start.marker.position;
    const clickEnd = end.marker.position;

    // Vertical / height measurement → nothing to smooth; use the raw clicks so the
    // ill-conditioned projection can't blow up the distance (Smoothed = Standard).
    if (isNearVertical(clickStart, clickEnd)) {
        return {
            start: { x: clickStart.x, y: clickStart.y, z: clickStart.z },
            end: { x: clickEnd.x, y: clickEnd.y, z: clickEnd.z },
        };
    }

    const dx = rawEnd.x - rawStart.x;
    const dy = rawEnd.y - rawStart.y;
    const run = Math.hypot(dx, dy);
    if (run < 1e-6) {
        // Horizontally coincident — slope undefined; keep the raw medians.
        return { start: { ...rawStart }, end: { ...rawEnd } };
    }

    const ux = dx / run;
    const uy = dy / run;
    const slope = (rawEnd.z - rawStart.z) / run;

    const project = (
        click: { x: number; y: number; z: number },
        raw: { x: number; y: number; z: number }
    ) => {
        // Signed horizontal offset from the raw median to the click, along the line.
        const d = (click.x - raw.x) * ux + (click.y - raw.y) * uy;
        return { x: click.x, y: click.y, z: raw.z + slope * d };
    };

    return {
        start: project(clickStart, rawStart),
        end: project(clickEnd, rawEnd),
    };
}
